import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, Search, Loader2, AlertTriangle, ShieldAlert, CheckSquare, Square } from "lucide-react";

/**
 * DataCleanTab — Super-Admin-only bulk cleanup for Customer / Vendor data.
 *
 * Lists real records straight from the production DB (via the super-admin-
 * guarded `/api/admin/data-clean/*` endpoints), lets the operator search +
 * paginate + select, and requires typing "DELETE" before any destructive
 * bulk delete is dispatched. The backend re-validates the same string.
 */

const PAGE_SIZE = 50;

const DataCleanTab = ({ auth, authAxios }) => {
  const [section, setSection] = useState("customers");

  if (auth?.role !== "admin") {
    return (
      <div className="bg-white rounded-2xl p-10 border border-red-100 text-center" data-testid="data-clean-forbidden">
        <ShieldAlert className="mx-auto mb-3 text-red-500" size={36} />
        <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">Super Admin access only</h2>
        <p className="text-sm text-[#64748B]">This tool is restricted to the Super Admin account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-clean-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1B4B] flex items-center gap-2">
            <Trash2 className="text-red-500" size={22} /> Data Clean
          </h1>
          <p className="text-sm text-[#64748B] mt-1 max-w-xl">
            Bulk-delete customer or vendor records directly from the live database.
            This action is permanent and only touches the explicitly selected rows.
          </p>
        </div>
        <div className="inline-flex rounded-xl bg-white border border-gray-200 p-1 shadow-sm">
          <button
            onClick={() => setSection("customers")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${section === "customers" ? "bg-[#DC283C] text-white" : "text-[#64748B] hover:bg-gray-50"}`}
            data-testid="data-clean-section-customers"
          >Customers</button>
          <button
            onClick={() => setSection("vendors")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${section === "vendors" ? "bg-[#DC283C] text-white" : "text-[#64748B] hover:bg-gray-50"}`}
            data-testid="data-clean-section-vendors"
          >Vendors</button>
        </div>
      </div>

      {section === "customers" ? (
        <CleanTable key="customers" kind="customers" authAxios={authAxios} />
      ) : (
        <CleanTable key="vendors" kind="vendors" authAxios={authAxios} />
      )}
    </div>
  );
};

const COLUMN_DEFS = {
  customers: {
    endpoint: "customers",
    idField: "customer_id",
    fallbackIdField: "phone",
    searchPlaceholder: "Search by name, email, phone, customer ID…",
    filters: [], // customers have no additional server filters wired up
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "source", label: "Source", formatter: fmtSource },
      { key: "customer_id", label: "Customer ID", mono: true },
      { key: "created_at", label: "First Seen", formatter: fmtDate },
      { key: "total_bookings", label: "Bookings" },
    ],
    noun: "customer",
    nounPlural: "customers",
  },
  vendors: {
    endpoint: "vendors",
    idField: "id",
    searchPlaceholder: "Search by name, email, phone, vendor ID, service type…",
    filters: [
      { param: "status", label: "Status", options: ["pending", "approved", "rejected", "suspended"] },
    ],
    columns: [
      { key: "name", label: "Vendor Name" },
      { key: "id", label: "Vendor ID", mono: true },
      { key: "service_type", label: "Service Type" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status", formatter: fmtStatus },
    ],
    noun: "vendor",
    nounPlural: "vendors",
  },
};

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return v; }
}
function fmtStatus(v) {
  if (!v) return "—";
  const map = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700", suspended: "bg-gray-200 text-gray-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${map[v] || "bg-gray-100 text-gray-700"}`}>{v}</span>;
}
function fmtSource(v) {
  if (!v) return "—";
  const map = { account: "bg-indigo-100 text-indigo-700", booking: "bg-emerald-100 text-emerald-700", lead: "bg-amber-100 text-amber-700", whatsapp: "bg-green-100 text-green-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${map[v] || "bg-gray-100 text-gray-700"}`}>{v}</span>;
}

const CleanTable = ({ kind, authAxios }) => {
  const def = COLUMN_DEFS[kind];
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedSearch) params.q = debouncedSearch;
      Object.entries(filterValues).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await authAxios.get(`/api/admin/data-clean/${def.endpoint}`, { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load records");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [authAxios, def.endpoint, page, debouncedSearch, filterValues]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [kind, debouncedSearch, filterValues, page]);

  const getRowId = useCallback((row) => row[def.idField] || row[def.fallbackIdField] || "", [def.idField, def.fallbackIdField]);
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(getRowId(r)));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) { rows.forEach((r) => next.delete(getRowId(r))); }
      else { rows.forEach((r) => { const id = getRowId(r); if (id) next.add(id); }); }
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedList = useMemo(() => Array.from(selected), [selected]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openConfirm = () => {
    if (selectedList.length === 0) return;
    setConfirmText("");
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    setError("");
    try {
      const { data } = await authAxios.post(
        `/api/admin/data-clean/${def.endpoint}/delete`,
        { ids: selectedList, confirm: "DELETE" },
      );
      setConfirmOpen(false);
      setSelected(new Set());
      await load();
      alert(`Deleted ${data.deleted} ${data.deleted === 1 ? def.noun : def.nounPlural}.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`data-clean-${kind}`}>
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={def.searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#DC283C]/40"
            data-testid={`data-clean-${kind}-search`}
          />
        </div>
        {def.filters.map((f) => (
          <select
            key={f.param}
            value={filterValues[f.param] || ""}
            onChange={(e) => { setFilterValues((v) => ({ ...v, [f.param]: e.target.value })); setPage(1); }}
            className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm"
            data-testid={`data-clean-${kind}-filter-${f.param}`}
          >
            <option value="">All {f.label}</option>
            {f.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        ))}
        <div className="text-xs text-[#64748B] whitespace-nowrap">
          {total.toLocaleString()} total · {selectedList.length} selected
        </div>
        <button
          onClick={openConfirm}
          disabled={selectedList.length === 0}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${selectedList.length === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-red-600 text-white hover:bg-red-700 shadow-sm"}`}
          data-testid={`data-clean-${kind}-delete-btn`}
        >
          <Trash2 size={14} /> Delete Selected ({selectedList.length})
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-700 text-sm" data-testid={`data-clean-${kind}-error`}>{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button onClick={toggleAll} className="flex items-center" data-testid={`data-clean-${kind}-toggle-all`}>
                  {allSelected ? <CheckSquare size={16} className="text-[#DC283C]" /> : <Square size={16} className="text-[#94a3b8]" />}
                </button>
              </th>
              {def.columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wide">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={def.columns.length + 1} className="py-10 text-center text-[#94a3b8]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={def.columns.length + 1} className="py-10 text-center text-[#94a3b8]">No records match.</td></tr>
            ) : rows.map((row) => {
              const id = getRowId(row);
              const checked = selected.has(id);
              return (
                <tr key={id || JSON.stringify(row).slice(0, 24)} className={`border-t border-gray-100 ${checked ? "bg-pink-50/40" : "hover:bg-gray-50"}`} data-testid={`data-clean-${kind}-row-${id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => id && toggleOne(id)} data-testid={`data-clean-${kind}-select-${id}`}>
                      {checked ? <CheckSquare size={16} className="text-[#DC283C]" /> : <Square size={16} className="text-[#94a3b8]" />}
                    </button>
                  </td>
                  {def.columns.map((c) => {
                    const raw = row[c.key];
                    const display = c.formatter ? c.formatter(raw) : (raw ?? "—");
                    return (
                      <td key={c.key} className={`px-4 py-3 text-[#1E1B4B] ${c.mono ? "font-mono text-xs" : ""}`}>{display || "—"}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="text-[#64748B]">Page {page} of {totalPages}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              data-testid={`data-clean-${kind}-prev`}>Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              data-testid={`data-clean-${kind}-next`}>Next</button>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid={`data-clean-${kind}-confirm-modal`}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-red-600" size={22} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1E1B4B]">Permanently delete {selectedList.length} {selectedList.length === 1 ? def.noun : def.nounPlural}?</h3>
                <p className="text-sm text-[#64748B] mt-1">{kind === "customers"
                  ? "FULL PURGE — this removes the selected customers everywhere: account, leads, all their bookings, party-tracking, and CRM/WhatsApp history. This cannot be undone."
                  : "This action cannot be undone. Only the selected rows will be removed from the database."}</p>
              </div>
            </div>
            <ul className="mb-4 max-h-32 overflow-y-auto text-xs font-mono text-[#64748B] bg-gray-50 rounded-lg p-3 space-y-1">
              {selectedList.slice(0, 20).map((id) => <li key={id}>• {id}</li>)}
              {selectedList.length > 20 && <li>…and {selectedList.length - 20} more</li>}
            </ul>
            <label className="block text-xs font-semibold text-[#64748B] mb-1.5">Type <span className="text-red-600 font-mono">DELETE</span> to confirm:</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
              autoFocus
              data-testid={`data-clean-${kind}-confirm-input`}
            />
            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setConfirmOpen(false); setError(""); }}
                disabled={deleting}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-[#64748B] hover:bg-gray-100"
                data-testid={`data-clean-${kind}-cancel`}
              >Cancel</button>
              <button
                onClick={doDelete}
                disabled={confirmText !== "DELETE" || deleting}
                className={`px-4 py-2 rounded-xl text-sm font-bold text-white transition-all ${confirmText === "DELETE" && !deleting ? "bg-red-600 hover:bg-red-700" : "bg-red-300 cursor-not-allowed"}`}
                data-testid={`data-clean-${kind}-confirm-btn`}
              >
                {deleting ? <><Loader2 className="inline animate-spin mr-1" size={14} />Deleting…</> : `Delete ${selectedList.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataCleanTab;
